import React, { useState } from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs'; // ← CORREGIDO: 'exceljs' no 'exbook'

const ExportarDetalleProyecto = ({ 
  nombreProyecto,
  participantes,
  participantesConArchivos,
  participantesSinArchivos,
  totalParticipantes,
  totalArchivos
}) => {
  const [exporting, setExporting] = useState(false);

  const getTotalArchivos = (participante) => {
    if (!participante.resultados || Object.keys(participante.resultados).length === 0) return 0;
    return Object.values(participante.resultados).reduce((sum, proyecto) => {
      return sum + (proyecto.total || 0);
    }, 0);
  };

  const calcularPorcentaje = (valor, total) => {
    if (!total || total === 0) return 0;
    return Math.round((valor / total) * 100);
  };

  const exportarAExcel = async () => {
    setExporting(true);
    
    try {
      const workbook = new ExcelJS.Workbook();
      
      // Hoja 1: Resumen General
      const worksheetResumen = workbook.addWorksheet('Resumen Proyecto');
      
      // Hoja 2: Participantes con Archivos
      const worksheetConArchivos = workbook.addWorksheet('Con Visitas');
      
      // Hoja 3: Participantes sin Archivos
      const worksheetSinArchivos = workbook.addWorksheet('Sin Visitas');
      
      // Hoja 4: Detalle Completo
      const worksheetDetalle = workbook.addWorksheet('Detalle Completo');
      
      // Estilos comunes
      const estiloTitulo = {
        font: { bold: true, size: 16, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '2C3E50' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloSubtitulo = {
        font: { bold: true, size: 14 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ECF0F1' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloCabecera = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '3498DB' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloCelda = {
        alignment: { vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloCeldaCentrada = {
        ...estiloCelda,
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloMetricaVerde = {
        ...estiloCeldaCentrada,
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '10B981' } }
      };

      const estiloMetricaRojo = {
        ...estiloCeldaCentrada,
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EF4444' } }
      };

      const estiloMetricaAzul = {
        ...estiloCeldaCentrada,
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '3B82F6' } }
      };

      // --- HOJA 1: RESUMEN PROYECTO ---
      worksheetResumen.mergeCells('A1:D1');
      const tituloResumen = worksheetResumen.getCell('A1');
      tituloResumen.value = `DETALLE DEL PROYECTO: ${nombreProyecto}`;
      tituloResumen.style = estiloTitulo;
      
      // Fecha de generación
      const fecha = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      worksheetResumen.mergeCells('A2:D2');
      worksheetResumen.getCell('A2').value = `Generado el: ${fecha}`;
      worksheetResumen.getCell('A2').style = {
        font: { italic: true, color: { argb: '7F8C8D' } },
        alignment: { horizontal: 'left' }
      };
      
      worksheetResumen.addRow([]);

      // Métricas principales
      worksheetResumen.mergeCells('A4:D4');
      worksheetResumen.getCell('A4').value = 'MÉTRICAS DEL PROYECTO';
      worksheetResumen.getCell('A4').style = estiloSubtitulo;

      const metricas = [
        { label: 'Total de Participantes', valor: totalParticipantes, estilo: estiloCeldaCentrada },
        { label: 'Participantes con al menos una visita', valor: `${participantesConArchivos.length} (${calcularPorcentaje(participantesConArchivos.length, totalParticipantes)}%)`, estilo: estiloMetricaVerde },
        { label: 'Participantes sin visitas registradas', valor: `${participantesSinArchivos.length} (${calcularPorcentaje(participantesSinArchivos.length, totalParticipantes)}%)`, estilo: estiloMetricaRojo },
        { label: 'Total de visitas registradas', valor: totalArchivos, estilo: estiloMetricaAzul }
      ];

      metricas.forEach((metrica, index) => {
        const fila = 5 + index;
        worksheetResumen.getCell(`A${fila}`).value = metrica.label;
        worksheetResumen.getCell(`A${fila}`).style = {
          ...estiloCelda,
          font: { bold: true },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8F9F9' } }
        };
        
        worksheetResumen.mergeCells(`B${fila}:D${fila}`);
        worksheetResumen.getCell(`B${fila}`).value = metrica.valor;
        worksheetResumen.getCell(`B${fila}`).style = metrica.estilo;
      });

      // Distribución por departamento
      if (participantes.length > 0) {
        worksheetResumen.addRow([]);
        worksheetResumen.mergeCells(`A${10}:D${10}`);
        worksheetResumen.getCell(`A${10}`).value = 'DISTRIBUCIÓN POR DEPARTAMENTO';
        worksheetResumen.getCell(`A${10}`).style = estiloSubtitulo;

        const distribucionDepartamentos = participantes.reduce((acc, p) => {
          const depto = p.departamento || 'No especificado';
          acc[depto] = (acc[depto] || 0) + 1;
          return acc;
        }, {});

        let filaActual = 11;
        Object.entries(distribucionDepartamentos).forEach(([depto, cantidad]) => {
          const porcentaje = calcularPorcentaje(cantidad, totalParticipantes);
          worksheetResumen.getCell(`A${filaActual}`).value = depto;
          worksheetResumen.getCell(`B${filaActual}`).value = cantidad;
          worksheetResumen.getCell(`C${filaActual}`).value = `${porcentaje}%`;
          
          // Barra de progreso simple
          worksheetResumen.getCell(`D${filaActual}`).value = '█'.repeat(Math.min(Math.floor(porcentaje / 5), 20));
          worksheetResumen.getCell(`D${filaActual}`).style = {
            font: { color: { argb: '3498DB' } },
            alignment: { horizontal: 'left' }
          };
          
          filaActual++;
        });
      }

      // Ajustar anchos
      worksheetResumen.columns = [
        { width: 35 },
        { width: 15 },
        { width: 15 },
        { width: 25 }
      ];

      // --- HOJA 2: PARTICIPANTES CON ARCHIVOS ---
      worksheetConArchivos.mergeCells('A1:E1');
      worksheetConArchivos.getCell('A1').value = `PARTICIPANTES CON VISITAS (${participantesConArchivos.length})`;
      worksheetConArchivos.getCell('A1').style = estiloTitulo;

      const cabeceraConArchivos = ['#', 'Nombre', 'Cédula', 'Municipio', 'Total de Visitas'];
      const filaCabeceraCon = worksheetConArchivos.addRow(cabeceraConArchivos);
      filaCabeceraCon.eachCell((cell) => {
        cell.style = estiloCabecera;
      });

      participantesConArchivos.forEach((participante, index) => {
        const totalVisitas = getTotalArchivos(participante);
        const fila = [
          index + 1,
          participante.nombre || '',
          participante.cedula || '',
          participante.municipio || '',
          totalVisitas
        ];

        const filaDatos = worksheetConArchivos.addRow(fila);
        filaDatos.eachCell((cell, colNumber) => {
          cell.style = estiloCeldaCentrada;
          
          // Destacar total de visitas
          if (colNumber === 5) {
            if (totalVisitas >= 10) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D5F4E6' } };
              cell.font = { bold: true };
            } else if (totalVisitas >= 5) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9C4' } };
            }
          }
        });
      });

      // Total de visitas
      const totalVisitasConArchivos = participantesConArchivos.reduce((sum, p) => sum + getTotalArchivos(p), 0);
      const filaTotalCon = worksheetConArchivos.addRow(['', '', '', 'TOTAL VISITAS:', totalVisitasConArchivos]);
      filaTotalCon.eachCell((cell) => {
        cell.style = {
          ...estiloCeldaCentrada,
          font: { bold: true },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E3F2FD' } }
        };
      });

      worksheetConArchivos.columns = [
        { width: 6 },
        { width: 35 },
        { width: 15 },
        { width: 20 },
        { width: 15 }
      ];

      // --- HOJA 3: PARTICIPANTES SIN ARCHIVOS ---
      worksheetSinArchivos.mergeCells('A1:E1');
      worksheetSinArchivos.getCell('A1').value = `PARTICIPANTES SIN VISITAS (${participantesSinArchivos.length})`;
      worksheetSinArchivos.getCell('A1').style = {
        ...estiloTitulo,
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EF4444' } }
      };

      const cabeceraSinArchivos = ['#', 'Nombre', 'Cédula', 'Municipio', 'Estado'];
      const filaCabeceraSin = worksheetSinArchivos.addRow(cabeceraSinArchivos);
      filaCabeceraSin.eachCell((cell) => {
        cell.style = estiloCabecera;
      });

      participantesSinArchivos.forEach((participante, index) => {
        const fila = [
          index + 1,
          participante.nombre || '',
          participante.cedula || '',
          participante.municipio || '',
          'SIN VISITAS'
        ];

        const filaDatos = worksheetSinArchivos.addRow(fila);
        filaDatos.eachCell((cell, colNumber) => {
          cell.style = estiloCeldaCentrada;
          
          // Colorear celda de estado
          if (colNumber === 5) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEBEE' } };
            cell.font = { bold: true, color: { argb: 'EF4444' } };
          }
        });
      });

      worksheetSinArchivos.columns = [
        { width: 6 },
        { width: 35 },
        { width: 15 },
        { width: 20 },
        { width: 15 }
      ];

      // --- HOJA 4: DETALLE COMPLETO ---
      worksheetDetalle.mergeCells('A1:H1');
      worksheetDetalle.getCell('A1').value = `DETALLE COMPLETO DEL PROYECTO: ${nombreProyecto}`;
      worksheetDetalle.getCell('A1').style = estiloTitulo;

      const cabeceraDetalle = ['#', 'Nombre', 'Cédula', 'Departamento', 'Municipio', 'Total Visitas', 'Estado', 'Meta del Participante'];
      const filaCabeceraDetalle = worksheetDetalle.addRow(cabeceraDetalle);
      filaCabeceraDetalle.eachCell((cell) => {
        cell.style = estiloCabecera;
      });

      participantes.forEach((participante, index) => {
        const totalVisitas = getTotalArchivos(participante);
        const tieneArchivos = totalVisitas > 0;
        const estado = tieneArchivos ? 'CON VISITAS' : 'SIN VISITAS';
        const meta = parseFloat(participante.meta_x_participante) || 0;
        const porcentajeCumplido = meta > 0 ? Math.round((totalVisitas / meta) * 100) : 0;

        const fila = [
          index + 1,
          participante.nombre || '',
          participante.cedula || '',
          participante.departamento || '',
          participante.municipio || '',
          totalVisitas,
          estado,
          meta
        ];

        const filaDatos = worksheetDetalle.addRow(fila);
        filaDatos.eachCell((cell, colNumber) => {
          cell.style = estiloCeldaCentrada;
          
          // Colorear según estado
          if (colNumber === 7) { // Estado
            if (tieneArchivos) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E8F5E9' } };
              cell.font = { color: { argb: '2E7D32' } };
            } else {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEBEE' } };
              cell.font = { color: { argb: 'C62828' } };
            }
          }
          
          // Colorear según cumplimiento de meta
          if (colNumber === 8 && meta > 0) { // Meta
            if (porcentajeCumplido >= 100) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'C8E6C9' } };
            } else if (porcentajeCumplido >= 50) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9C4' } };
            } else {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0B2' } };
            }
          }
        });
      });

      // Totales y promedios
      const promedioVisitas = totalParticipantes > 0 ? (totalArchivos / totalParticipantes).toFixed(1) : 0;
      const filaResumenDetalle = worksheetDetalle.addRow([
        '', '', '', '', '',
        `Promedio: ${promedioVisitas} visitas/participante`,
        '',
        ''
      ]);
      
      filaResumenDetalle.eachCell((cell) => {
        if (cell.col === 6) {
          cell.style = {
            ...estiloCeldaCentrada,
            font: { bold: true, italic: true },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F3F4F6' } }
          };
        }
      });

      worksheetDetalle.columns = [
        { width: 6 },
        { width: 30 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 15 },
        { width: 15 }
      ];

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const nombreArchivo = `Detalle_Proyecto_${nombreProyecto.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      link.href = url;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL
      setTimeout(() => URL.revokeObjectURL(url), 100);
      
    } catch (error) {
      console.error('Error al exportar:', error);
      alert('Error al exportar el archivo. Por favor, intente de nuevo.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      onClick={exportarAExcel}
      loading={exporting}
      disabled={!participantes || participantes.length === 0}
      title="Exportar detalle del proyecto"
      style={{ marginTop: 16 }}
    >
      Exportar Excel
    </Button>
  );
};

export default ExportarDetalleProyecto;